//import clsx from "clsx";
//import { useState } from "react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/scrollbar";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useHiddenChallengeRewards } from "../../common/hooks/useHiddenChallengeRewards";
import type {
  ChallengeVariant,
} from "../../components/Cards/ChallengeCard";
import ChallengeCard from "../../components/Cards/ChallengeCard";
import Skeleton from "../../components/Common/Skeleton";
import StyledPagination from "../../components/Common/StyledPagination";
import Popup from "../../components/Popup";
import { useGetChallengesQuery } from "../../configs/apis/GetChallenges";
import type { Challenge } from "../../configs/apis/GetChallenges/models";
import { useGetProgressQuery } from "../../configs/apis/GetProgress";
import type { Progress } from "../../configs/apis/GetProgress/models";
import type { Prize } from "../../configs/apis/models";
import { useUpdateIsClaimedMutation } from "../../configs/apis/UpdateIsClaimed";
import {
  getActiveMilestonePrize,
  getShowablePrize,
  isPendingOrProcessingPrize,
} from "../../configs/utils/utility";

const PAGE_SIZE = 8;
type AllChallengesPageProps = {
  isInProgress?: boolean;
};

const AllChallengesPage = ({
  isInProgress = false,
}: AllChallengesPageProps) => {
  const { t: translate } = useTranslation("translation");
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupReward, setPopupReward] = useState<Prize | undefined>();
  const [claimableChallengeId, setClaimableChallengeId] = useState<number | undefined>();
  const [currentPage, setCurrentPage] = useState(1);

  const [updateIsClaimed, { isLoading: isClaiming }] = useUpdateIsClaimedMutation();

  const {
    data: forYouData,
    isLoading: isForYouLoading,
    isFetching: isForYouFetching,
    refetch: refetchForYou,
  } = useGetChallengesQuery(
    {
      page: currentPage,
      itemsPerPage: PAGE_SIZE,
      categoryId: undefined,
    },
    { skip: isInProgress },
  );

  const {
    data: inProgressData,
    isLoading: inProgressLoading,
    isFetching: inProgressFetching,
    refetch: refetchInProgress,
  } = useGetProgressQuery(
    {
      categoryId: undefined,
      page: currentPage,
      itemsPerPage: PAGE_SIZE,
    },
    { skip: !isInProgress },
  );

  // Passing undefined when !isInProgress is only safe because the two tabs are distinct
  // routes, so switching remounts this page and resets the queue instead of stranding it.
  const {
    visibleChallenges: visibleInProgress,
    queuedRewards,
    dismissFrontOfQueue,
    markChallengeClaimed,
  } = useHiddenChallengeRewards(isInProgress ? inProgressData?.data : undefined);
  const frontOfQueue = queuedRewards[0];

  const rawChallengeData = isInProgress ? inProgressData?.data : forYouData?.data;
  const challengeData = isInProgress
    ? visibleInProgress
    : (forYouData?.data as Challenge[]);

  const getVariant = (card: Challenge): ChallengeVariant => {
    if (isInProgress) {
      // Mirrors Slider.getVariant — keep the two in step.
      const milestonePrizes = (card as Progress).prizes;
      if (getShowablePrize(milestonePrizes) || isPendingOrProcessingPrize(milestonePrizes)) {
        return "claimable";
      }
      return "inProgress";
    }

    if (card.isLocked) return "locked";
    return "active";
  };

  return (
    <>
      <Popup
        isVisible={isPopupVisible}
        onClose={() => {
          setIsPopupVisible(false);
          setPopupReward(undefined);

          if (claimableChallengeId) {
            updateIsClaimed({
              challengeId: claimableChallengeId,
            })
              .unwrap()
              .then(() => {
                if (isInProgress) {
                  refetchInProgress();
                } else {
                  refetchForYou();
                }
              })
              .catch(() => {})
              .finally(() => {
                setClaimableChallengeId(undefined);
              });
          }
        }}
        prize={popupReward}
      />
      <Popup
        isVisible={queuedRewards.length > 0 && !isPopupVisible}
        onClose={() => {
          const closingChallengeId = frontOfQueue?.challengeId;
          dismissFrontOfQueue();

          if (closingChallengeId) {
            updateIsClaimed({
              challengeId: closingChallengeId,
            })
              .unwrap()
              .then(() => {
                markChallengeClaimed(closingChallengeId);
                refetchInProgress();
              })
              .catch(() => {});
          }
        }}
        prize={frontOfQueue?.prize}
      />
      <header className="z-10 mb-8 pt-4">
        <div className="mb-8 flex items-center justify-start">
          <h1 className="pointer-events-none text-center text-4xl font-semibold">
            {isInProgress ? translate("in_progress") : translate("challenges_pager")}
          </h1>
        </div>
      </header>
      <main className="mt-12">
        <div className="grid grid-cols-1 gap-5 px-5 md:grid-cols-2 lg:grid-cols-4">
          {(isForYouLoading ||
            isForYouFetching ||
            inProgressLoading ||
            inProgressFetching) ? (
              Array.from({ length: PAGE_SIZE }).map((_, idx) => <Skeleton key={idx} />)
            ) : (
              challengeData?.map((card) => {
                const activePrize = isInProgress
                  ? getActiveMilestonePrize((card as Progress).prizes)
                  : undefined;
                // In-progress cards nest prizes one level deeper. The for-you branch is
                // unreachable in practice — those variants render no claim button.
                const showablePrize = isInProgress
                  ? getShowablePrize((card as Progress).prizes)
                  : card.prizes?.at(0);

                return (
                  <ChallengeCard
                    key={card.id}
                    {...card}
                    categoryName={card.category?.name ?? ""}
                    isClaimingThis={isClaiming && claimableChallengeId === card.id}
                    onClaim={() => {
                      // Unreachable via getVariant; guards against an empty popup that
                      // would still consume the reward on close.
                      if (!showablePrize) return;

                      setClaimableChallengeId(card.id);
                      setPopupReward(showablePrize);
                      setIsPopupVisible(true);
                    }}
                    prize={activePrize}
                    progress={
                      isInProgress
                        ? (card as Progress)?.averageProgress
                        : undefined
                    }
                    skipImmediateClaim={true}
                    variant={getVariant(card)}
                  />
                );
              })
            )}
        </div>
        {rawChallengeData && rawChallengeData.length > 0 && (
          <div className="mt-8 flex justify-center">
            <StyledPagination
              current={currentPage}
              onChange={(newPage) => setCurrentPage(newPage)}
              pageSize={PAGE_SIZE}
              total={
                isInProgress
                  ? (inProgressData?.count ?? 0)
                  : (forYouData?.count ?? 0)
              }
            />
          </div>
        )}
      </main>
    </>
  );
};

export default AllChallengesPage;
